import React from 'react';
import { Phone } from 'lucide-react';

// Keypad button component
const KeypadButton = ({ number, letters, onClick }) => (
    <button
        onClick={() => onClick(number)}
        className="keypad-button flex flex-col items-center justify-center w-20 h-20 rounded-full bg-gray-700/80 focus:outline-none active:bg-gray-600"
    >
        <span className="text-4xl font-light">{number}</span>
        {letters && <span className="text-xs tracking-widest uppercase">{letters}</span>}
    </button>
);

// Keypad layout configuration
const keypadLayout = [
    { number: '1', letters: '' }, { number: '2', letters: 'ABC' }, { number: '3', letters: 'DEF' },
    { number: '4', letters: 'GHI' }, { number: '5', letters: 'JKL' }, { number: '6', letters: 'MNO' },
    { number: '7', letters: 'PQRS' }, { number: '8', letters: 'TUV' }, { number: '9', letters: 'WXYZ' },
    { number: '*', letters: '' }, { number: '0', letters: '+' }, { number: '#', letters: '' },
];

// Helper to format duration
const formatDuration = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

/**
 * PhoneCallUI - Pure presentational component for the in-call screen
 *
 * @param {Object} props
 * @param {string} props.contactName - Name of the contact being called
 * @param {string} props.callStatus - Current call status ('Calling...', 'Connected', etc.)
 * @param {number} props.callDuration - Call duration in seconds (only shown when connected)
 * @param {string} props.voiceState - Voice interaction state: 'idle' | 'listening' | 'processing' | 'speaking'
 * @param {boolean} props.needsUserTap - Whether iOS needs user tap to play audio
 * @param {boolean} props.showKeypad - Whether to show the keypad
 * @param {string} props.dialedNumber - Numbers dialed on keypad
 * @param {boolean} props.showAIControls - Whether to show AI status indicators
 * @param {Function} props.onKeypadPress - Callback when keypad button is pressed
 * @param {Function} props.onEndCall - Callback when end call button is pressed
 * @param {Function} props.onScreenTap - Callback for screen tap (iOS audio workaround)
 */
const PhoneCallUI = ({
    contactName = 'Unknown Caller',
    callStatus = 'Calling...',
    callDuration = 0,
    voiceState = 'idle',
    needsUserTap = false,
    showKeypad = false,
    dialedNumber = '',
    showAIControls = false,
    onKeypadPress = () => {},
    onEndCall = () => {},
    onScreenTap = () => {},
}) => {
    // Determine status indicator color based on voice state
    const getStatusColor = () => {
        switch (voiceState) {
            case 'processing':
                return 'bg-yellow-400 animate-pulse';
            case 'listening':
                return 'bg-green-400 animate-pulse';
            case 'speaking':
                return 'bg-blue-400';
            default:
                return 'bg-gray-400';
        }
    };

    // Determine status text based on voice state
    const getStatusText = () => {
        if (needsUserTap) return 'Tap to hear response';
        switch (voiceState) {
            case 'processing':
                return 'Thinking';
            case 'listening':
                return 'Listening';
            case 'speaking':
                return 'Speaking';
            default:
                return 'Idle';
        }
    };

    return (
        <div
            className="bg-gray-900 text-white h-full flex flex-col items-center justify-between p-6 overflow-auto"
            onClick={needsUserTap ? onScreenTap : undefined}
        >
            {/* Contact Info */}
            <div className="text-center pt-8">
                <h1 className="text-4xl font-light">{contactName}</h1>
                <p className="text-gray-400 mt-1">
                    {callStatus === 'Connected' ? formatDuration(callDuration) : callStatus}
                </p>
                {/* Show AI status for calls with agent */}
                {showAIControls && (
                    <div className="mt-3 mb-2">
                        <div className="flex items-center justify-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${getStatusColor()}`}></div>
                            <p className="text-sm text-gray-400">
                                {getStatusText()}
                            </p>
                        </div>
                        {needsUserTap && (
                            <p className="text-xs text-yellow-400 mt-2 animate-pulse font-medium">
                                Tap screen to play audio
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* Middle Controls Area - Keypad */}
            <div className="w-full max-w-xs">
                {showKeypad ? (
                    <div className="flex flex-col items-center">
                        <div className="h-10 flex items-center mb-2">
                            <p className="text-3xl font-thin tracking-wider">{dialedNumber}</p>
                        </div>
                        <div className="grid grid-cols-3 gap-x-4 gap-y-2 w-full mb-4">
                            {keypadLayout.map(key => (
                                <KeypadButton
                                    key={key.number}
                                    {...key}
                                    onClick={onKeypadPress}
                                />
                            ))}
                        </div>
                    </div>
                ) : (
                    // Space placeholder when keypad is not visible
                    <div className="h-[280px]"></div>
                )}
            </div>

            {/* Bottom End Call Button */}
            <div className="w-full max-w-xs pb-4">
                <button
                    onClick={onEndCall}
                    className="w-16 h-16 flex items-center justify-center bg-red-500 rounded-full mx-auto hover:bg-red-600 transition-colors"
                >
                    <Phone size={28} className="transform rotate-[135deg]" />
                </button>
            </div>
        </div>
    );
};

export default PhoneCallUI;
